import React from "react";
import { useBooleanState } from ".//useBooleanState";
import { useDidUpdate } from ".//useDidUpdate";

// based on from https://github.com/streamich/react-use/blob/master/src/useIntersection.ts

const useIntersection = (
  ref: React.RefObject<HTMLElement | null>,
  options: IntersectionObserverInit & { skip?: boolean } = {},
): IntersectionObserverEntry | null => {
  const [intersectionObserverEntry, setIntersectionObserverEntry] =
    React.useState<IntersectionObserverEntry | null>(null);

  useDidUpdate(() => {
    const { skip, ...observerOptions } = options;
    if (ref.current && !skip && typeof IntersectionObserver === "function") {
      const handler = (entries: IntersectionObserverEntry[]) => {
        if (entries[0]) {
          setIntersectionObserverEntry(entries[0]);
        } else {
          setIntersectionObserverEntry(null);
        }
      };

      const observer = new IntersectionObserver(handler, observerOptions);
      observer.observe(ref.current);

      return () => {
        setIntersectionObserverEntry(null);
        observer.disconnect();
      };
    }
    return undefined;
  }, [ref.current, options.threshold, options.root, options.rootMargin]);

  return intersectionObserverEntry;
};

useIntersection.canObserve = typeof IntersectionObserver === "function";
export default useIntersection;

export function useLazyLoading<RefElement extends HTMLElement = HTMLDivElement>() {
  const ref = React.useRef<RefElement>(null);
  const [shown, show] = useBooleanState(!useIntersection.canObserve);
  const intersection = useIntersection(ref, {
    // Stop observing intersection once data is shown
    skip: shown,
  });
  React.useEffect(() => {
    if (intersection?.isIntersecting) {
      show();
    }
  });

  // Ref should be attached to a dom element at least while shown is false
  return { shown, ref };
}
